// MCIP-23 — the slice matrix: every (region × environment) world in one
// glance, ABOVE the bifurcated pod cards. The design rule it embodies
// (docs/design/multi-region.md): mixed for ORIENTATION — "which world needs
// me" — never for analysis; clicking a tile IS the slice switch, and
// everything below re-renders against that one world.
import { pct } from '../../lib/format';

function tileState(s) {
  if (s.incident_clusters > 0) return 'incident';
  if (!s.harvested) return 'empty';
  return 'ok';
}

export default function SliceMatrix({ matrix, region, environment, onSelectSlice }) {
  if (!matrix || matrix.slices.length === 0) return null;
  const environments = matrix.environments;
  const regions = matrix.regions;
  const byKey = new Map(matrix.slices.map((s) => [`${s.region}/${s.environment}`, s]));

  return (
    <section className="card slice-matrix" aria-label="Regions and environments at a glance">
      <div className="card__head">
        <div>
          <h2 className="card__title">All worlds at a glance</h2>
          <p className="card__subtitle">
            each region × environment is an independent picture — pick the one that needs you;
            the cards below show only that world
          </p>
        </div>
      </div>
      <div
        className="slice-matrix__grid"
        style={{ gridTemplateColumns: `auto repeat(${environments.length}, minmax(120px, 1fr))` }}
      >
        <span aria-hidden="true" />
        {environments.map((e) => (
          <span key={e} className="slice-matrix__col">{e}</span>
        ))}
        {regions.map((r) => (
          [
            <span key={`${r}-label`} className="slice-matrix__row">{r.toUpperCase()}</span>,
            ...environments.map((e) => {
              const s = byKey.get(`${r}/${e}`);
              if (!s) return <span key={`${r}-${e}`} />;
              const state = tileState(s);
              const active = r === region && e === environment;
              return (
                <button
                  key={`${r}-${e}`}
                  type="button"
                  className={`slice-tile slice-tile--${state}${active ? ' is-active' : ''}`}
                  onClick={() => onSelectSlice(r, e)}
                  aria-pressed={active}
                  title={`${r.toUpperCase()}/${e} — ${s.jobs} jobs · ${s.runs_in_window} runs in window`}
                >
                  {state === 'empty' ? (
                    <span className="slice-tile__none">no data</span>
                  ) : (
                    <>
                      <span className="slice-tile__rate">
                        {s.pass_rate_latest === null ? '—' : pct(s.pass_rate_latest)}
                      </span>
                      <span className="slice-tile__meta">
                        {s.runs_in_window} run{s.runs_in_window === 1 ? '' : 's'}
                        {s.open_clusters > 0 && ` · ${s.open_clusters} signals`}
                      </span>
                      {s.incident_clusters > 0 && (
                        <span className="slice-tile__incident">
                          {s.incident_clusters} incident{s.incident_clusters === 1 ? '' : 's'}
                        </span>
                      )}
                    </>
                  )}
                </button>
              );
            }),
          ]
        ))}
      </div>
    </section>
  );
}
